import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { apiClient } from "@/lib/apiClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users, UserCheck, UserX, AlertTriangle, Percent } from "lucide-react";
import { cn } from "@/lib/utils";

interface Painel {
  dataReferencia: string;
  justificativasPendentes: number;
  funcionariosAtivos: number;
  bateramNoDia: number;
  ausentesNoDia: number;
  percentualAssiduidade: number;
}
interface Ausente {
  idFuncionario: number;
  nome: string;
  sMatricula: string;
  sDepartamento: string;
  jornadaNome: string;
  diaPrevisto: boolean;
}

function Metrica({ titulo, valor, sub, icon: Icon, cor, onClick, ativo }: {
  titulo: string; valor: string | number; sub?: string; icon: any; cor: string; onClick?: () => void; ativo?: boolean;
}) {
  return (
    <Card
      className={cn(onClick && "cursor-pointer transition-colors hover:bg-muted/40", ativo && "border-primary")}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === "Enter" || e.key === " ") onClick(); } : undefined}
    >
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{titulo}</CardTitle>
        <Icon className={`h-5 w-5 ${cor}`} />
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-bold">{valor}</div>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

export default function PainelPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(format(new Date(), "yyyy-MM-dd"));
  const [verAusentes, setVerAusentes] = useState(false);
  const ehHoje = data === format(new Date(), "yyyy-MM-dd");
  const sufixo = ehHoje ? "hoje" : "no dia";

  const { data: p, isLoading } = useQuery({
    queryKey: ["ponto-painel", data],
    queryFn: () => apiClient.get<Painel>("/api/rh/ponto/painel", { params: { dataRef: data } }).then((r) => r.data),
    enabled: !!data,
  });
  const { data: ausentes = [], isLoading: carregandoAusentes } = useQuery({
    queryKey: ["ponto-ausentes", data],
    queryFn: () => apiClient.get<Ausente[]>("/api/rh/ponto/painel/ausentes", { params: { dataRef: data } }).then((r) => r.data),
    enabled: verAusentes && !!data,
  });

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold">Painel de Ponto</h1>
          <p className="text-sm text-muted-foreground">Assiduidade e pendências do dia.</p>
        </div>
        <div className="space-y-1">
          <Label htmlFor="painel-data">Data de referência</Label>
          <Input id="painel-data" type="date" value={data} onChange={(e) => setData(e.target.value)} className="w-44" />
        </div>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Carregando…</p>
      ) : !p ? (
        <p className="text-muted-foreground">Sem dados.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Metrica titulo="Assiduidade do dia" valor={`${p.percentualAssiduidade}%`} sub={`${p.bateramNoDia} de ${p.funcionariosAtivos} bateram`} icon={Percent} cor="text-primary" />
          <Metrica titulo="Funcionários ativos" valor={p.funcionariosAtivos} sub="abrir cadastro" icon={Users} cor="text-blue-500"
            onClick={() => navigate("/rh/ponto/funcionarios")} />
          <Metrica titulo={`Bateram ${sufixo}`} valor={p.bateramNoDia} icon={UserCheck} cor="text-green-600" />
          <Metrica titulo={`Ausentes ${sufixo}`} valor={p.ausentesNoDia} sub={verAusentes ? "ocultar lista" : "ver quem não bateu"} icon={UserX} cor="text-orange-500"
            onClick={() => setVerAusentes((v) => !v)} ativo={verAusentes} />
          <Metrica titulo="Justificativas pendentes" valor={p.justificativasPendentes} sub="abrir aprovação de ajustes" icon={AlertTriangle} cor="text-amber-500"
            onClick={() => navigate("/rh/ponto/ajustes")} />
        </div>
      )}

      {verAusentes && (
        <Card>
          <CardHeader><CardTitle className="text-base">Sem batida em {format(new Date(`${data}T00:00:00`), "dd/MM/yyyy")}</CardTitle></CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Matrícula</TableHead>
                    <TableHead>Funcionário</TableHead>
                    <TableHead>Departamento</TableHead>
                    <TableHead>Jornada</TableHead>
                    <TableHead>Escala do dia</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {carregandoAusentes ? (
                    <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">Carregando…</TableCell></TableRow>
                  ) : ausentes.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">Todos os funcionários bateram ponto.</TableCell></TableRow>
                  ) : ausentes.map((a) => (
                    <TableRow key={a.idFuncionario} className={!a.diaPrevisto ? "opacity-60" : undefined}>
                      <TableCell>{a.sMatricula || "—"}</TableCell>
                      <TableCell>{a.nome}</TableCell>
                      <TableCell>{a.sDepartamento || "—"}</TableCell>
                      <TableCell>{a.jornadaNome || "sem jornada"}</TableCell>
                      <TableCell>
                        {a.diaPrevisto
                          ? <Badge variant="destructive">trabalho previsto</Badge>
                          : <Badge variant="outline">folga</Badge>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Funcionários dispensados de ponto não entram na lista.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
